import {
  quantityFill,
  quantitySvgFill,
} from '@/features/ai-models/identityFill';

import { magnitude } from './chartScale';
import { sumMoney } from './money';

/**
 * Where it went as share bars (spec 255): the rules, apart from the drawing so they
 * are tested on their own. One bar per dimension; a slice is one line, the lines past
 * the third as "N others", or a gap in the data.
 */

/** How many lines are named before the rest become "N others". */
export const NAMED_SLICES = 3;

/** The runs nobody started, said as a gap rather than as a person (spec 255). */
export const NO_PERSON_LABEL = 'No person · automatic runs';

/** A slice under this share of the money is listed, not drawn. */
const DRAWN_SHARE = 0.01;

/** One line of a dimension, whichever read it came from. */
export interface ShareLine {
  /** Unique within the dimension; null for the one line that names a gap. */
  key: string | null;
  label: string;
  amount: string;
  tokens: number;
  runs: number;
  unmeasured: number;
  unpriced: number;
  /** A line that names a gap in the data: no person, profile not recorded. */
  unknown: boolean;
  /**
   * What the line names is gone but its spend still counts: a deleted workflow or
   * profile (specs 146, 258). Absent means live.
   */
  deleted?: boolean;
}

export type SliceKind = 'named' | 'others' | 'unknown';

export interface Slice<Line extends ShareLine = ShareLine> {
  /** Stable within the dimension: the line's key, "others", or "unknown". */
  id: string;
  kind: SliceKind;
  /** The line's name, or "2 others". */
  label: string;
  /** One line for a named or unknown slice; every line past the third for others. */
  lines: Line[];
  amount: string;
  value: number;
  /** Of the dimension's money, 0 to 1; 0 when nothing was spent. */
  share: number;
  /** "46%", "<1%", or null when there is no money to share. */
  shareText: string | null;
  /** Brand blue for the leading slice, grey steps after it, faint for a gap. */
  fill: string;
  /** The same fill for an SVG mark (spec 257). */
  svgFill: string;
  /** Whether the bar draws it: a slice under 1% is listed only. */
  drawn: boolean;
  /** A named slice whose line is deleted, so its name wears the Deleted marker. */
  deleted: boolean;
}

/** "46%", "<1%"; null when the slice or the whole has no money. */
export function shareText(value: number, total: number): string | null {
  if (total <= 0 || value <= 0) return null;
  const share = value / total;
  if (share < DRAWN_SHARE) return '<1%';
  return `${String(Math.round(share * 100))}%`;
}

/**
 * A dimension's slices (spec 255): the lines that name something in the service's
 * order, at most three of them named and the rest as "N others", then the gap last.
 * Grey first: the leading slice alone takes brand blue, so colour marks what leads.
 */
export function shareSlices<Line extends ShareLine>(
  lines: Line[],
): Slice<Line>[] {
  const total = lines.reduce((sum, line) => sum + magnitude(line.amount), 0);
  const known = lines.filter((line) => !line.unknown);
  const gaps = lines.filter((line) => line.unknown);
  const named = known.slice(0, NAMED_SLICES);
  const rest = known.slice(NAMED_SLICES);

  const slice = (
    kind: SliceKind,
    id: string,
    label: string,
    group: Line[],
    rank: number | null,
  ): Slice<Line> => {
    const value = group.reduce((sum, line) => sum + magnitude(line.amount), 0);
    const amount =
      group.length === 1
        ? group[0].amount
        : (sumMoney(group.map((line) => line.amount)) ?? String(value));
    const share = total > 0 ? value / total : 0;
    return {
      id,
      kind,
      label,
      lines: group,
      amount,
      value,
      share,
      shareText: shareText(value, total),
      fill: quantityFill(rank),
      svgFill: quantitySvgFill(rank),
      drawn: share >= DRAWN_SHARE,
      deleted: kind === 'named' && group[0]?.deleted === true,
    };
  };

  return [
    ...named.map((line, rank) =>
      slice(
        'named',
        line.key ?? `line-${String(rank)}`,
        line.label,
        [line],
        rank,
      ),
    ),
    ...(rest.length > 0
      ? [
          slice(
            'others',
            'others',
            `${String(rest.length)} ${rest.length === 1 ? 'other' : 'others'}`,
            rest,
            NAMED_SLICES,
          ),
        ]
      : []),
    ...gaps.map((line, index) =>
      slice('unknown', `unknown-${String(index)}`, line.label, [line], null),
    ),
  ];
}

/** "3 workflows", "1 person". */
export function entryCount(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}
